// Deterministic models of project permissions for Lesson 2.10 ("Decide What Each Person May Do").
// They apply the lesson's ACTIONS table, task owners and refusal rules to the shared dataset;
// they do not query a database or send requests.

import { COOKIE, LOG_IN_FIRST } from './session-models';

export type Person = 'maya' | 'ravi' | 'jo';
export type Role = 'manager' | 'member';
export interface LabPerson {
  id: number;
  name: string;
  /** The person's label in the graphics' selects and matrix columns. */
  label: string;
  /** Role in Apollo (project 1); null means no membership row. */
  role: Role | null;
}
export const people: Record<Person, LabPerson> = {
  maya: { id: 1, name: 'Maya', label: 'Maya (manager)', role: 'manager' },
  ravi: { id: 2, name: 'Ravi', label: 'Ravi (member)', role: 'member' },
  jo: { id: 3, name: 'Jo', label: 'Jo (not a member)', role: null },
};
export const PEOPLE: readonly Person[] = ['maya', 'ravi', 'jo'];

export type Action =
  'read' | 'create_task' | 'complete_task' | 'assign_task' | 'delete_task' | 'manage_members';
/** The lesson's ACTIONS table: each action names the roles allowed to perform it. */
export const ACTIONS: Record<Action, readonly Role[]> = {
  read: ['manager', 'member'],
  create_task: ['manager', 'member'],
  complete_task: ['manager', 'member'],
  assign_task: ['manager'],
  delete_task: ['manager'],
  manage_members: ['manager'],
};
export const ACTION_ORDER = Object.keys(ACTIONS) as Action[];
export const actionLabels: Record<Action, string> = {
  read: 'Read the project',
  create_task: 'Create a task',
  complete_task: 'Complete a task',
  assign_task: 'Assign a task',
  delete_task: 'Delete a task',
  manage_members: 'Manage members',
};
/** allowed(role, action) is `role in ACTIONS[action]`; None (not a member) is never allowed. */
export const allowed = (role: Role | null, action: Action) =>
  role !== null && ACTIONS[action].includes(role);
/** A role set as Python prints it, such as {"manager", "member"}. */
export const pythonSet = (roles: readonly Role[]) =>
  `{${roles.map((role) => `"${role}"`).join(', ')}}`;
export const FORBIDDEN_BODY = '{"detail":"Only a manager of this project can do that."}';
export const PROJECT_NOT_FOUND = '{"detail":"Project was not found"}';
export const TASK_NOT_FOUND = '{"detail":"Task was not found"}';
const JSON_TYPE = 'content-type: application/json';
const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);
const pythonRole = (role: Role | null) => (role === null ? 'None' : `"${role}"`);

// --- perm-matrix: what each person may do in Apollo ---------------------------------------

export type Verdict = 'Allowed' | '403' | '404';
export function verdict(person: Person, action: Action, checkRoles: boolean): Verdict {
  if (!checkRoles) return 'Allowed';
  const { role } = people[person];
  if (role === null) return '404';
  return allowed(role, action) ? 'Allowed' : '403';
}
export interface MatrixRow {
  action: Action;
  label: string;
  /** One verdict per person, in PEOPLE order. */
  cells: Verdict[];
}
export const permissionMatrix = (checkRoles: boolean): MatrixRow[] =>
  ACTION_ORDER.map((action) => ({
    action,
    label: actionLabels[action],
    cells: PEOPLE.map((person) => verdict(person, action, checkRoles)),
  }));
export function roleFact(person: Person) {
  const { role } = people[person];
  return role === null ? 'None (no membership row)' : `"${role}"`;
}
export function matrixMessage(person: Person, action: Action, checkRoles: boolean) {
  const { name, role } = people[person];
  const doing = lower(actionLabels[action]);
  const rule = `ACTIONS["${action}"] = ${pythonSet(ACTIONS[action])}`;
  if (!checkRoles) {
    const who: Record<Person, string> = {
      maya: `Maya may ${doing}, and so may Ravi and Jo.`,
      ravi: `Ravi, a member, may ${doing}.`,
      jo: `Jo, who is not in Apollo, may ${doing}.`,
    };
    return `Allowed. Without a role check the server asks only who is signed in, so every cell says Allowed. ${who[person]}`;
  }
  if (role === null)
    return `404 Not Found. ${name} has no membership in Apollo, so role_in returns None, which no action allows. The answer is the same as for a project that does not exist.`;
  if (allowed(role, action))
    return `Allowed. ${name} is a ${role} of Apollo, and ${rule} includes "${role}".`;
  return `403 Forbidden. ${name} is a ${role} of Apollo, but ${rule} does not include "${role}". The server answers ${FORBIDDEN_BODY}.`;
}

// --- perm-ownership: whose tasks the task page shows -------------------------------------

export interface LabTask {
  id: number;
  title: string;
  /** 1 for Apollo; null for a personal task. */
  projectId: 1 | null;
  /** The owner of a personal task once migration 4 adds owner_id; null for project tasks. */
  owner: Person | null;
  /** Who a project task is assigned to; null when unassigned or personal. */
  assignee: Person | null;
  completed: boolean;
}
export const TASKS: readonly LabTask[] = [
  {
    id: 1,
    title: 'Prepare the demo',
    projectId: null,
    owner: 'maya',
    assignee: null,
    completed: false,
  },
  {
    id: 4,
    title: "Read Maya's notes",
    projectId: null,
    owner: 'maya',
    assignee: null,
    completed: true,
  },
  {
    id: 8,
    title: 'Check the examples',
    projectId: null,
    owner: 'maya',
    assignee: null,
    completed: false,
  },
  {
    id: 9,
    title: 'Write the release note',
    projectId: 1,
    owner: null,
    assignee: 'ravi',
    completed: false,
  },
  { id: 10, title: 'Check the dates', projectId: 1, owner: null, assignee: null, completed: false },
  {
    id: 11,
    title: 'Book the room',
    projectId: null,
    owner: 'jo',
    assignee: null,
    completed: false,
  },
];
/** Which condition puts a task on the viewer's page. */
export type Admission = 'shared list' | 'owner' | 'member of Apollo' | 'hidden';
export function admission(task: LabTask, viewer: Person, owners: boolean): Admission {
  if (!owners) return 'shared list';
  if (task.projectId === null) return task.owner === viewer ? 'owner' : 'hidden';
  return people[viewer].role === null ? 'hidden' : 'member of Apollo';
}
export const visibleTasks = (viewer: Person, owners: boolean) =>
  TASKS.filter((task) => admission(task, viewer, owners) !== 'hidden');
export const visibleTaskIds = (viewer: Person, owners: boolean) =>
  visibleTasks(viewer, owners).map((task) => task.id);
export function ownershipColumns(viewer: Person, owners: boolean) {
  return [
    'id',
    'title',
    'project_id',
    ...(owners ? ['owner_id'] : []),
    `Shown to ${people[viewer].name}`,
  ];
}
export function ownershipRows(viewer: Person, owners: boolean): string[][] {
  return TASKS.map((task) => [
    String(task.id),
    task.title,
    task.projectId === null ? 'NULL' : '1 (Apollo)',
    ...(owners
      ? [task.owner === null ? 'NULL' : `${people[task.owner].id} (${people[task.owner].name})`]
      : []),
    admission(task, viewer, owners),
  ]);
}
const taskLine = (task: LabTask) =>
  `${String(task.id).padEnd(4)}${task.title} (${task.completed ? 'completed' : 'open'})`;
export function taskPage(viewer: Person, owners: boolean) {
  const tasks = visibleTasks(viewer, owners);
  return [
    `GET /tasks → ${tasks.length} task${tasks.length === 1 ? '' : 's'}`,
    ...tasks.map(taskLine),
  ];
}
export const ownershipRule = (owners: boolean) =>
  owners
    ? 'A personal task shows only to its owner; a project task shows to members of its project.'
    : 'Every task shows to every signed-in person.';
export function ownershipMessage(viewer: Person, owners: boolean) {
  if (!owners) {
    const extra: Record<Person, string> = {
      maya: "Maya's page shows all six tasks, including Jo's task 11.",
      ravi: "Ravi's page shows all six tasks, including Maya's personal tasks 1, 4 and 8 and Jo's task 11.",
      jo: "Jo's page shows all six tasks, including Maya's personal tasks 1, 4 and 8 and Apollo's tasks 9 and 10, although he is in no project.",
    };
    return `${extra[viewer]} The old list has no rule: without owner_id nothing records whose personal task it is, and nothing limits project tasks to members.`;
  }
  const messages: Record<Person, string> = {
    maya: "Maya sees tasks 1, 4, 8, 9 and 10: her personal tasks as their owner, and Apollo's tasks as a member of Apollo. Jo's task 11 is hidden from her.",
    ravi: "Ravi sees tasks 9 and 10 as a member of Apollo. Maya's tasks 1, 4 and 8 and Jo's task 11 are hidden, and opening one by its ID answers 404 Task was not found.",
    jo: "Jo sees only his own task 11. He is in no project, so Apollo's tasks 9 and 10 are hidden along with Maya's tasks 1, 4 and 8.",
  };
  return messages[viewer];
}

// --- perm-client-only: hiding the button versus checking on the server --------------------

export type Client = 'page' | 'curl';
export const clientLabels: Record<Client, string> = { page: 'The page', curl: 'curl' };
export const NO_DELETE_BUTTON = "No Delete button on Ravi's page";
export const LAUNCH_TASKS = TASKS.filter((task) => task.projectId === 1);
export const assigneeName = (task: LabTask) =>
  task.assignee === null ? 'unassigned' : people[task.assignee].name;
export const CURL_COMMAND = 'curl -i -X DELETE -b ravi.txt http://127.0.0.1:8000/tasks/10';
export function clientView(client: Client) {
  if (client === 'page')
    return {
      label: "Ravi's page",
      lines: [
        'Apollo · your role: member',
        ...LAUNCH_TASKS.map(
          (task) =>
            `${taskLine(task)} · ${task.assignee === null ? 'unassigned' : `assigned to ${assigneeName(task)}`}`,
        ),
        NO_DELETE_BUTTON,
      ],
    };
  return {
    label: "curl with Ravi's cookie",
    lines: [
      `$ ${CURL_COMMAND}`,
      '',
      'DELETE /tasks/10 HTTP/1.1',
      `cookie: ${COOKIE}=<Ravi's token>`,
    ],
  };
}
export interface DeleteAttempt {
  client: Client;
  serverChecks: boolean;
  server: string[];
  response: string[];
  /** null when no request left the client. */
  status: 204 | 403 | null;
  /** Apollo's tasks after the attempt. */
  tasksAfter: LabTask[];
}
export interface ClientOnlyState {
  client: Client;
  serverChecks: boolean;
  attempt: DeleteAttempt | null;
  message: string;
}
export type ClientOnlyAction =
  | { kind: 'set-client'; client: Client }
  | { kind: 'set-server-checks'; on: boolean }
  | { kind: 'delete' }
  | { kind: 'reset' };
const PROMPT = 'Press Ravi deletes task 10 to send the attempt from this client.';
export const initialClientOnly = (): ClientOnlyState => ({
  client: 'page',
  serverChecks: false,
  attempt: null,
  message: `Ravi is a member of Apollo, so his page hides Delete. Every attempt starts from the lesson data, where Apollo has tasks 9 and 10. ${PROMPT}`,
});
export function deleteAttempt(client: Client, serverChecks: boolean): DeleteAttempt {
  if (client === 'page')
    return {
      client,
      serverChecks,
      server: ['(no request arrived)'],
      response: ['(no response)'],
      status: null,
      tasksAfter: [...LAUNCH_TASKS],
    };
  if (!serverChecks)
    return {
      client,
      serverChecks,
      server: ['current_user → Ravi (user 2)', 'DELETE FROM tasks WHERE id = 10  → 1 row removed'],
      response: ['HTTP/1.1 204 No Content'],
      status: 204,
      tasksAfter: LAUNCH_TASKS.filter((task) => task.id !== 10),
    };
  return {
    client,
    serverChecks,
    server: [
      'current_user → Ravi (user 2)',
      'task 10 is in project 1 (Apollo)',
      'role_in(connection, 1, 2) → "member"',
      'allowed("member", "delete_task") → False',
      'raise Forbidden → nothing deleted',
    ],
    response: ['HTTP/1.1 403 Forbidden', JSON_TYPE, '', FORBIDDEN_BODY],
    status: 403,
    tasksAfter: [...LAUNCH_TASKS],
  };
}
function attemptMessage(attempt: DeleteAttempt) {
  if (attempt.client === 'page')
    return attempt.serverChecks
      ? `${NO_DELETE_BUTTON}, so no request leaves the browser. The server check only matters for a client that ignores the page, such as curl.`
      : `${NO_DELETE_BUTTON}, so no request leaves the browser and task 10 stays. That protects the page, not the server: switch the client to curl.`;
  if (attempt.status === 204)
    return "curl sent DELETE /tasks/10 with Ravi's cookie. The server checked only that he is signed in and deleted the task: 204, and task 10 is gone. Hiding the button never stopped this request.";
  return 'The server looked up Ravi\'s role in Apollo, found "member" and refused: 403. Task 10 stays. The rule now holds for every client, the page and curl alike.';
}
export function clientOnlyTransition(
  state: ClientOnlyState,
  action: ClientOnlyAction,
): ClientOnlyState {
  switch (action.kind) {
    case 'reset':
      return initialClientOnly();
    case 'set-client':
      return {
        ...state,
        client: action.client,
        attempt: null,
        message:
          action.client === 'curl'
            ? `curl sends whatever request it is given, with Ravi's cookie from ravi.txt. ${PROMPT}`
            : `The page shows Delete only to managers. ${PROMPT}`,
      };
    case 'set-server-checks':
      return {
        ...state,
        serverChecks: action.on,
        attempt: null,
        message: action.on
          ? `The server now looks up the sender's role in the task's project before deleting. ${PROMPT}`
          : `The server now checks only that someone is signed in. ${PROMPT}`,
      };
    case 'delete': {
      const attempt = deleteAttempt(state.client, state.serverChecks);
      return { ...state, attempt, message: attemptMessage(attempt) };
    }
  }
}

// --- perm-status: which status each refusal gets -----------------------------------------

export type StatusRequest = 'no-cookie-read' | 'jo-read' | 'ravi-delete' | 'maya-delete';
export interface StatusRequestSpec {
  label: string;
  /** null: the request carries no cookie. */
  person: Person | null;
  action: 'read' | 'delete_task';
}
export const statusRequests: Record<StatusRequest, StatusRequestSpec> = {
  'no-cookie-read': { label: 'No cookie reads Apollo', person: null, action: 'read' },
  'jo-read': { label: 'Jo reads Apollo', person: 'jo', action: 'read' },
  'ravi-delete': { label: 'Ravi deletes task 10', person: 'ravi', action: 'delete_task' },
  'maya-delete': { label: 'Maya deletes task 10', person: 'maya', action: 'delete_task' },
};
export const STATUS_REQUEST_ORDER = Object.keys(statusRequests) as StatusRequest[];
export const CHECKS = ['Signed in?', 'Member of Apollo?', 'Role allows it?'] as const;
export type CheckOutcome = 'yes' | 'no' | 'not-reached';
export interface PathStep {
  label: (typeof CHECKS)[number];
  outcome: CheckOutcome;
  /** Empty when the check is not reached. */
  detail: string;
}
export type StatusCode = 200 | 204 | 401 | 403 | 404;
export interface StatusDecision {
  request: string[];
  steps: PathStep[];
  /** Index of the first failing check, or null when every check passes. */
  stoppedAt: number | null;
  status: StatusCode;
  statusText: string;
  response: string[];
  reveals: string;
}
const STATUS_TEXT: Record<StatusCode, string> = {
  200: '200 OK',
  204: '204 No Content',
  401: '401 Unauthorized',
  403: '403 Forbidden',
  404: '404 Not Found',
};
const jsonResponse = (status: StatusCode, body: string) => [
  `HTTP/1.1 ${STATUS_TEXT[status]}`,
  JSON_TYPE,
  '',
  body,
];
/** The decision path for one person and action; null means the request carries no cookie. */
export function decide(person: Person | null, action: StatusRequestSpec['action']): StatusDecision {
  const isRead = action === 'read';
  const requestLines = [
    isRead ? 'GET /projects/1/tasks HTTP/1.1' : 'DELETE /tasks/10 HTTP/1.1',
    ...(person ? [`cookie: ${COOKIE}=<${people[person].name}'s token>`] : []),
  ];
  const step = (index: number, outcome: CheckOutcome, detail: string): PathStep => ({
    label: CHECKS[index] ?? CHECKS[0],
    outcome,
    detail,
  });
  const notReached = (from: number) =>
    CHECKS.slice(from).map((_, offset) => step(from + offset, 'not-reached', ''));
  if (person === null)
    return {
      request: requestLines,
      steps: [
        step(0, 'no', `No ${COOKIE} cookie, so current_user finds no session.`),
        ...notReached(1),
      ],
      stoppedAt: 0,
      status: 401,
      statusText: STATUS_TEXT[401],
      response: jsonResponse(401, LOG_IN_FIRST),
      reveals:
        '401 tells the client only to log in first. The server has not looked at Apollo yet, so the answer reveals nothing about it.',
    };
  const { id, name, role } = people[person];
  const signedIn = step(
    0,
    'yes',
    `The cookie names ${name}'s session: current_user is user ${id}.`,
  );
  const where = isRead ? '' : 'Task 10 is in Apollo. ';
  const lookup = `${where}role_in(connection, 1, ${id}) returns ${pythonRole(role)}`;
  if (role === null)
    return {
      request: requestLines,
      steps: [
        signedIn,
        step(1, 'no', `${lookup}: ${name} has no membership row in Apollo.`),
        ...notReached(2),
      ],
      stoppedAt: 1,
      status: 404,
      statusText: STATUS_TEXT[404],
      response: jsonResponse(404, isRead ? PROJECT_NOT_FOUND : TASK_NOT_FOUND),
      reveals: `404 reveals nothing about Apollo to ${name}: a project that does not exist gets exactly the same answer, so ${name} cannot even tell that Apollo exists.`,
    };
  const member = step(1, 'yes', `${lookup}.`);
  const rule = `ACTIONS["${action}"] is ${pythonSet(ACTIONS[action])}`;
  if (!allowed(role, action))
    return {
      request: requestLines,
      steps: [
        signedIn,
        member,
        step(2, 'no', `allowed("${role}", "${action}") is False: ${rule}.`),
      ],
      stoppedAt: 2,
      status: 403,
      statusText: STATUS_TEXT[403],
      response: jsonResponse(403, FORBIDDEN_BODY),
      reveals: `403 tells ${name} nothing new: as a ${role} of Apollo ${name} already sees ${isRead ? 'Apollo' : 'task 10'}, and the answer adds only that the role does not allow ${isRead ? 'reading it' : 'deleting it'}.`,
    };
  const success: StatusCode = isRead ? 200 : 204;
  return {
    request: requestLines,
    steps: [signedIn, member, step(2, 'yes', `allowed("${role}", "${action}") is True: ${rule}.`)],
    stoppedAt: null,
    status: success,
    statusText: STATUS_TEXT[success],
    response: isRead
      ? jsonResponse(200, '[{"id":9,"title":"Write the release note",…},{"id":10,…}]')
      : [`HTTP/1.1 ${STATUS_TEXT[204]}`],
    reveals: isRead
      ? `200 returns Apollo's tasks to ${name}, a ${role} of Apollo.`
      : `204 confirms that task 10 was deleted, which ${name}, a ${role} of Apollo, may know and do.`,
  };
}
export const statusDecision = (request: StatusRequest) =>
  decide(statusRequests[request].person, statusRequests[request].action);
export function stepOutcomeText(step: PathStep, status: StatusCode) {
  if (step.outcome === 'yes') return 'Yes';
  if (step.outcome === 'no') return `No → ${status}`;
  return 'Not reached';
}
